import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AgreementRefreshedDocument,
  PerformanceAgreementTrainingRequestsDocument,
  PerformanceAgreementTrainingsDocument,
  PerformanceProcessAgreementCourseDocument,
  PerformanceProcessAgreementPageDocument,
  PerformanceProcessAgreementQuestionnairesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage, ResponsivePageBanner } from 'modules/layout/components/ResponsivePage'
import { ActionItemList } from 'modules/performance_process/components/agreements/ActionItemList'
import { AgreementCourse } from 'modules/performance_process/components/agreements/AgreementCourse'
import { AgreementConclusions } from 'modules/performance_process/components/agreements/Conclusions'
import { usePrintableAgreement } from 'modules/performance_process/components/agreements/PrintableAgreement'
import { SectionableQuestions } from 'modules/performance_process/components/agreements/SectionableQuestions'

import { useBuildTransactionalActions } from './transactionalActions'

const ReviewAgreementPage = () => {
  const { agreementId } = useParams()

  if (!agreementId) {
    throw new Error('Agreement ID is required')
  }

  const { access: currentAccess } = useCurrentSession()

  const { data, loading, error, refetch } = useQuery(PerformanceProcessAgreementPageDocument, {
    variables: { id: agreementId },
  })

  const {
    data: questionnairesData,
    loading: questionnairesLoading,
    error: questionnairesError,
  } = useQuery(PerformanceProcessAgreementQuestionnairesDocument, {
    variables: { id: agreementId },
  })

  const questionnaireAgreement = questionnairesData?.performance?.agreement
  const agreement = data?.performance?.agreement
  const client = useApolloClient()

  useSubscription(AgreementRefreshedDocument, {
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.performanceAgreementRefresh?.agreement?.id !== agreementId) {
        return
      }

      refetch()

      client.refetchQueries({
        include: [PerformanceProcessAgreementCourseDocument],
      })
    },
  })

  const transactionalActions = useBuildTransactionalActions({
    agreement,
    currentAccessId: currentAccess.id,
  })

  const {
    data: performanceAgreementTrainingsData,
    loading: loadingPerformanceAgreementTrainings,
    error: agreementTrainingsError,
  } = useQuery(PerformanceAgreementTrainingsDocument, {
    variables: { accessId: agreement?.target?.access?.id },
  })

  const {
    data: performanceAgreementTrainingRequestsData,
    loading: loadingPerformanceAgreementTrainingRequests,
    error: agreementTrainingRequetsError,
  } = useQuery(PerformanceAgreementTrainingRequestsDocument, {
    variables: { agreementId: agreementId },
  })

  const { pdf } = usePrintableAgreement(
    agreement,
    questionnaireAgreement,
    performanceAgreementTrainingRequestsData?.performance?.agreement,
    performanceAgreementTrainingsData?.trainings?.trainingsConnection?.nodes
  )

  const signedByYou =
    agreement &&
    ((agreement.managerSignedAt && agreement.signer?.id == currentAccess.id) ||
      (agreement.targetSignedAt && agreement.target?.access?.id == currentAccess.id))

  const banner: ResponsivePageBanner =
    agreement?.status === 'signed'
      ? {
          message: i18n.t('performance_process.agreements.agreement.signed_by_both_parties'),
          icon: 'signature',
          color: 'info',
        }
      : agreement?.locked
      ? {
          message: signedByYou
            ? i18n.t('performance_process.agreements.agreement.locked_signed_by_you')
            : i18n.t('performance_process.agreements.agreement.locked_signed_by', {
                name: agreement.signer?.fullName || agreement.target?.access?.fullName,
              }),
          icon: 'lock-locked',
          color: 'warning',
        }
      : undefined

  const actionItems = agreement?.actionItemsInfo

  return (
    <ResponsivePage.Body
      loading={
        loading ||
        questionnairesLoading ||
        loadingPerformanceAgreementTrainings ||
        loadingPerformanceAgreementTrainingRequests
      }
      error={
        error || questionnairesError || agreementTrainingsError || agreementTrainingRequetsError
      }
      showHeader={false}
      transactionalActions={transactionalActions}
      banner={banner}
      widget={
        pdf?.url &&
        agreement?.status === 'signed' && (
          <Button.Router
            download={`${agreement?.target?.access?.fullName}.pdf`}
            iconName="download"
            blankTarget
            disabled={pdf.loading}
            to={pdf.url}
          >
            {i18n.t('performance_process.agreements.actions.download')}
          </Button.Router>
        )
      }
    >
      {agreement && questionnaireAgreement && (
        <Box gap="s24">
          <SectionableQuestions agreement={questionnaireAgreement} />

          <Box gap="s24">
            {(!agreement?.locked || (actionItems?.length || 0) > 0) && (
              <>
                <Text.Header2 weight="semibold">
                  {i18n.t('performance_process.agreements.action_items.title')}
                </Text.Header2>
                <ActionItemList agreement={agreement} />
              </>
            )}

            <AgreementCourse agreement={agreement} HeaderComponent={Text.Header2} />

            {(!agreement.locked || agreement.conclusions) && (
              <>
                <Text.Header2 weight="semibold">
                  {i18n.t('performance_process.agreements.conclusions.title')}
                </Text.Header2>
                <AgreementConclusions agreement={agreement} />
              </>
            )}
          </Box>
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewAgreementPage
